import { LoginPage } from '@duke-hq/app-provider-web/LoginPage';
import { Routes, Route, useLocation, Outlet, Navigate } from 'react-router-dom';
import { ProviderMainLayout } from './App/ProviderMainLayout';
import { SelfSignup } from '@duke-hq/app-provider-web/SelfSignup';
import { SignOut } from '@duke-hq/app-provider-web/tenant/SignOut';
import { AssignPrimarySiteLocation } from '@duke-hq/app-provider-web/AssignPrimarySiteLocation';
import { StudentsPage } from './students-page/StudentsPage';
import { Enrolments } from '@duke-hq/app-provider-web/tenant/Enrolments/Enrolments';
import { PendingReferrals } from '@duke-hq/app-provider-web/tenant/Enrolments/Referrals/Pending/Page';
import { EnrolmentsLayout } from '@duke-hq/app-provider-web/tenant/Enrolments/EnrolmentsLayout';
import FiltersProvider from '@duke-hq/filters/context/FiltersProvider';
import { TeamPage } from './Team/TeamPage';
import { TeamMembers } from './Team/TeamMembers';
import { Dashboard } from './Dashboard/Dashboard';
import { SupportTicketsPage } from './SupportTickets/Page';
import { NewTicketPage } from './SupportTickets/NewTicketPage';
import { GroupRewardsPage } from './GroupRewards/GroupRewardsPage';
import { GroupRewardsCauses } from './GroupRewards/GroupRewardsCauses';
import { GroupRewardsAdmin } from './GroupRewards/GroupRewardsAdmin';
import { useFlag } from '@duke-hq/flags/react';
import {
  Center,
  Container,
  Flex,
  Heading,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import Demo from './Demo';
import HowToReferDemo from './HowToReferDemo';
import { useTenantContext } from './TenantContext';
import { TeamEnrolments } from './Team/TeamEnrolments';
import { FileUploadDemo } from './FileUploadDemo';
import { pxToRem } from '@duke-hq/ui/constants/scalable';
import { PlacementsStudents } from './WorkPlacements/Students';
import { trpcTenant } from './trpcTenant';

export function TenantRoutes() {
  const tenantCtx = useTenantContext();
  const INCENTIVE_PROGRAM_RELEASE = useFlag('INCENTIVE_PROGRAM_RELEASE');
  const WORK_PLACEMENT_FEATURE = useFlag('WORK_PLACEMENT_FEATURE');
  const permissions = tenantCtx?.permissions || [];
  const canManageTeamMembers = permissions.includes('canManageTeamMembers');
  const canReadInternalEnrolments = permissions.includes('canReadInternalEnrolments');

  // Only surface placements for providers who actually have work placements (mirrors the
  // dashboard), and only when the work-placement feature is on. The sidebar runs the same
  // query, so React Query dedupes it — clicking through from the nav is already warm.
  const { data: placementsData } = trpcTenant.workPlacements.hasPlacements.useQuery(undefined, {
    enabled: WORK_PLACEMENT_FEATURE === true,
  });
  const showPlacements = WORK_PLACEMENT_FEATURE === true && placementsData?.hasPlacements === true;

  return (
    <Routes>
      <Route element={<ProviderMainLayout />}>
        <Route path="/how-to-refer-demo" element={<HowToReferDemo />} />
        <Route path="/student-demo" element={<Demo />} />
        {tenantCtx?.environment === 'local' ? (
          <Route path="/file-upload-demo" element={<FileUploadDemo />} />
        ) : null}
        {canManageTeamMembers || canReadInternalEnrolments ? (
          <Route path="/team" element={<TeamPage />}>
            {canManageTeamMembers ? (
              <>
                <Route path="members/:consultantId" element={<TeamMembers />} />
                <Route path="members" element={<TeamMembers />} />
              </>
            ) : null}
            {canReadInternalEnrolments ? (
              <Route
                element={
                  <FiltersProvider localStorageKey="team_enrolments_page_filters">
                    <Outlet />
                  </FiltersProvider>
                }
              >
                <Route path="enrolments" element={<TeamEnrolments />} />
                <Route path="enrolment/:enrolmentId" element={<TeamEnrolments />} />
              </Route>
            ) : null}
          </Route>
        ) : null}
        <Route path="/enrolments" element={<EnrolmentsLayout />}>
          <Route index element={<Enrolments />} />
          <Route path="referrals" element={<PendingReferrals />} />
        </Route>
        <Route path="/enrolment/:enrolmentId" element={<EnrolmentsLayout />}>
          <Route index element={<Enrolments />} />
        </Route>
        <Route path="/students" element={<StudentsPage />} />
        <Route path="/student/:studentId/:tab?/:value?" element={<StudentsPage />} />
        {showPlacements ? (
          <>
            <Route path="/placements" element={<Navigate to="/placements/students" replace />} />
            <Route path="/placements/students" element={<PlacementsStudents />} />
          </>
        ) : null}
        <Route path="/inbox" element={<SupportTicketsPage />} />
        <Route path="/inbox/:ticketId" element={<SupportTicketsPage />} />
        <Route path="/inbox/new" element={<NewTicketPage />} />
        {(
          typeof INCENTIVE_PROGRAM_RELEASE === 'boolean'
            ? INCENTIVE_PROGRAM_RELEASE
            : tenantCtx?.settings?.incentiveProgramEnabled === true
        ) ? (
          <Route path="/group_rewards" element={<GroupRewardsPage />}>
            <Route index element={<GroupRewardsCauses />} />
            <Route path="settings" element={<GroupRewardsAdmin />} />
          </Route>
        ) : null}
        <Route path="/" element={<Dashboard />} />
        <Route path="*" element={<NoMatch />}></Route>
      </Route>
      <Route path="/activation" element={<AssignPrimarySiteLocation />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SelfSignup />} />
      <Route path="/signout" element={<SignOut />} />
    </Routes>
  );
}

function NoMatch() {
  const location = useLocation();

  return (
    <Center>
      <Flex flexDirection="column">
        <Heading>404</Heading>
        <br />
        <Text>
          No match for <code>{location.pathname}</code>
        </Text>
      </Flex>
    </Center>
  );
}

export function DemoPage() {
  // useFlag is a hook that returns the value of a feature flag
  const DEMO_FEATURE = useFlag('DEMO_FEATURE');
  const DEMO_RELEASE = useFlag('DEMO_RELEASE');
  const DEMO_EXPERIMENT = useFlag('DEMO_EXPERIMENT');

  return (
    <Center>
      <Container>
        <Heading>Demo Feature Flag</Heading>
        <Text>This page demonstrates how to use feature flags.</Text>

        <Table size="sm" marginTop={pxToRem(32)}>
          <Thead>
            <Tr>
              <Th>Flag</Th>
              <Th>Enabled</Th>
            </Tr>
          </Thead>
          <Tbody>
            <Tr>
              <Td>{DEMO_FEATURE ? '✅' : '❌'}</Td>
              <Td>
                <pre>DEMO_FEATURE</pre>
              </Td>
            </Tr>
            <Tr>
              <Td>{DEMO_RELEASE ? '✅' : '❌'}</Td>
              <Td>
                <pre>DEMO_RELEASE</pre>
              </Td>
            </Tr>
            <Tr>
              <Td>{DEMO_EXPERIMENT ? '✅' : '❌'}</Td>
              <Td>
                <pre>DEMO_EXPERIMENT</pre>
              </Td>
            </Tr>
          </Tbody>
        </Table>
      </Container>
    </Center>
  );
}
